/**
 * Date formatting utilities using Luxon
 */

import { DateTime } from "luxon";
import { FormatRegistry } from "./registry";
import type { DateFormat, FieldValue, FormattedValue } from "./types";

/**
 * Convert a value to a Luxon DateTime object.
 * Handles Date objects, ISO strings, and timestamps.
 *
 * @param value - Value to convert (Date, string, number, or null/undefined)
 * @returns Luxon DateTime object or null if invalid
 */
const toDateTime = (value: FieldValue): DateTime | null => {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }

  if (typeof value === "string") {
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt : null;
  }

  if (typeof value === "number") {
    // Assume it's a timestamp in milliseconds
    const dt = DateTime.fromMillis(value);
    return dt.isValid ? dt : null;
  }

  return null;
};

/**
 * Format date to a localized string with optional time and timezone.
 *
 * @param value - Date value (string, Date object, or timestamp)
 * @param showTime - Whether to include time in the output (default: false)
 * @param timezone - Optional timezone for display (e.g., "America/New_York")
 * @returns Formatted date string or null
 *
 * @example
 * ```typescript
 * toDateString(new Date()); // "12/25/2023"
 * toDateString(new Date(), true); // "12/25/2023, 3:30 PM"
 * toDateString("2023-12-25T15:30:00Z", true, "America/New_York"); // "12/25/2023, 10:30 AM"
 * ```
 */
export const toDateString = (
  value: string | Date | number | null | undefined,
  showTime: boolean = false,
  timezone?: string
): string | null => {
  if (!value) return null;

  const dt = toDateTime(value);
  if (!dt) return "Invalid Date";

  const dtWithZone = timezone ? dt.setZone(timezone) : dt;

  return showTime ? dtWithZone.toLocaleString(DateTime.DATETIME_SHORT) : dtWithZone.toLocaleString(DateTime.DATE_SHORT);
};

/**
 * Format date to relative time (e.g., "2 hours ago", "in 3 days").
 * Automatically chooses the most appropriate unit.
 *
 * @param value - Date value (string, Date object, or timestamp)
 * @returns Relative time string or null
 *
 * @example
 * ```typescript
 * toRelativeTime(new Date(Date.now() - 3600000)); // "1 hour ago"
 * toRelativeTime(new Date(Date.now() + 86400000)); // "in 1 day"
 * toRelativeTime(new Date()); // "now"
 * ```
 */
export const toRelativeTime = (value: string | Date | number | null | undefined): string | null => {
  if (!value) return null;

  const dt = toDateTime(value);
  if (!dt) return "Invalid Date";

  return dt.toRelative();
};

/**
 * Format date with full date and time including weekday
 */
export const toFullDateTime = (value: string | Date | number | null | undefined, timezone?: string): string | null => {
  if (!value) return null;

  const dt = toDateTime(value);
  if (!dt) return "Invalid Date";

  const dtWithZone = timezone ? dt.setZone(timezone) : dt;
  return dtWithZone.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

/**
 * Format date to ISO string
 */
export const toISOString = (value: string | Date | number | null | undefined): string | null => {
  if (!value) return null;

  const dt = toDateTime(value);
  if (!dt) return null;

  return dt.toISO();
};

/**
 * Format date with custom format string
 */
export const toCustomDateFormat = (
  value: string | Date | number | null | undefined,
  format: string,
  timezone?: string
): string | null => {
  if (!value) return null;

  const dt = toDateTime(value);
  if (!dt) return "Invalid Date";

  const dtWithZone = timezone ? dt.setZone(timezone) : dt;
  return dtWithZone.toFormat(format);
};

/**
 * Get formatted date parts
 */
export const getDateParts = (value: string | Date | number | null | undefined) => {
  const dt = toDateTime(value);
  if (!dt) return null;

  return {
    year: dt.year,
    month: dt.month,
    day: dt.day,
    weekday: dt.weekday,
    hour: dt.hour,
    minute: dt.minute,
    second: dt.second,
    millisecond: dt.millisecond,
    weekdayShort: dt.weekdayShort,
    weekdayLong: dt.weekdayLong,
    monthShort: dt.monthShort,
    monthLong: dt.monthLong,
  };
};

/**
 * Format date according to DateFormat specification
 */
export const formatDate = (value: FieldValue, format: DateFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (value == null) return format.nullText ?? null;

  const dt = toDateTime(value);
  if (!dt) return format.nullText ?? "Invalid Date";

  const dtWithZone = format.timezone ? dt.setZone(format.timezone) : dt;

  // Handle relative time
  if (format.relative) {
    return dtWithZone.toRelative();
  }

  // Handle custom format string
  if (format.format) {
    return dtWithZone.toFormat(format.format);
  }

  // Handle style-based formatting
  if (format.dateStyle || format.timeStyle) {
    const options: Record<string, unknown> = {};

    if (format.dateStyle) {
      switch (format.dateStyle) {
        case "short":
          options.date = DateTime.DATE_SHORT;
          break;
        case "medium":
          options.date = DateTime.DATE_MED;
          break;
        case "long":
          options.date = DateTime.DATE_FULL;
          break;
        case "full":
          options.date = DateTime.DATE_HUGE;
          break;
      }
    }

    if (format.timeStyle) {
      switch (format.timeStyle) {
        case "short":
          options.time = DateTime.TIME_SIMPLE;
          break;
        case "medium":
          options.time = DateTime.TIME_WITH_SECONDS;
          break;
        case "long":
        case "full":
          options.time = DateTime.TIME_WITH_SHORT_OFFSET;
          break;
      }
    }

    if (options.date && options.time) {
      return dtWithZone.toLocaleString({ ...options.date, ...options.time });
    } else if (options.date) {
      return dtWithZone.toLocaleString(options.date);
    } else if (options.time) {
      return dtWithZone.toLocaleString(options.time);
    }
  }

  // Default formatting
  return dtWithZone.toLocaleString(DateTime.DATETIME_SHORT);
};

// Register date formatters
FormatRegistry.register("date", (value, showTime, timezone) => {
  return toDateString(
    value as string | Date | number,
    typeof showTime === "boolean" ? showTime : false,
    typeof timezone === "string" ? timezone : undefined
  );
});

FormatRegistry.register("relativeTime", (value) => {
  return toRelativeTime(value as string | Date | number);
});

FormatRegistry.register("fullDateTime", (value, timezone) => {
  return toFullDateTime(value as string | Date | number, typeof timezone === "string" ? timezone : undefined);
});

FormatRegistry.register("isoString", (value) => {
  return toISOString(value as string | Date | number);
});

FormatRegistry.register("customDateFormat", (value, format, timezone) => {
  if (typeof format === "string") {
    return toCustomDateFormat(
      value as string | Date | number,
      format,
      typeof timezone === "string" ? timezone : undefined
    );
  }
  return null;
});
